import React, { useState } from 'react';
import { ArrowRight, FileText, FileType, Presentation, CheckCircle2 } from 'lucide-react';
import api from '../../../../../../services/api';
import CustomLoader from '../../../../../../shared/components/ui/CustomLoader';

/**
 * Component for the global submission actions at the bottom of the analysis page
 */
const GlobalSubmissionActions = ({
    activeStep,
    isTeamMember,
    isManager,
    allSectionsApproved,
    handleContinueToReview,
    isUpdating,
    isStageUpdating,
    isNoGo = false,
    isTeamLeadViewOnly = false,
    globalStatus,
    editableData,
    rfpId
}) => {
    const [downloading, setDownloading] = useState(null);
    const [error, setError] = useState('');
    const [selectedLang, setSelectedLang] = useState('EN');

    if (!isManager || isNoGo) return null;

    const handleDownload = async (format) => {
        setError('');
        setDownloading(format);
        if (!rfpId) {
            setError('RFP ID is missing.');
            setDownloading(null);
            return;
        }

        try {
            const url = `/rfp/report/${rfpId}?format=${format}&language=${selectedLang.toLowerCase()}`;
            const response = await api.post(url, {}, {
                responseType: 'blob',
                headers: {
                    'Accept-Language': selectedLang.toLowerCase(),
                    'language': selectedLang.toLowerCase()
                }
            });
            const blob = new Blob([response.data]);
            const ext = format === 'docx' ? 'docx' : 'pdf';
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `RFP_Report_${rfpId}.${ext}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error('Download error:', err);
            setError(`Download failed: ${err.message}`);
        } finally {
            setDownloading(null);
        }
    };

    const statusCode = Number(globalStatus);
    const statusLabel = (editableData?.global_status?.label || editableData?.proposal_stage || editableData?.PROPOSAL_STAGE || '').toLowerCase();

    const isAccepted = statusCode === 6 || statusLabel.includes('accept') || statusLabel.includes('won');
    const isRejected = statusCode === 7 || statusLabel.includes('reject') || statusLabel.includes('lost');
    const isSubmitted = statusCode === 5 || statusLabel.includes('submit');

    const isCompletedStatus = isAccepted || isRejected || isSubmitted;

    if (isCompletedStatus) {
        let msg = "This proposal has been submitted to the client.";
        if (isAccepted) msg = "This proposal has been accepted by the client.";
        if (isRejected) msg = "This proposal has been rejected by the client.";

        return (
            <div className="global-submission-actions-container" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.75rem 1.5rem',
                zIndex: 100,
                background: '#F0FDF4',
                borderTop: '1px solid #DCFCE7'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <CheckCircle2 size={18} style={{ color: '#16A34A' }} />
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#166534' }}>{msg}</span>
                        <span style={{ fontSize: '0.72rem', color: '#166534', opacity: 0.8 }}>
                            You can download the final proposal in your preferred format.
                        </span>
                        {error && <span style={{ fontSize: '0.72rem', color: '#DC2626', marginTop: '2px' }}>{error}</span>}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="global-submission-actions-container">
            <div className="global-actions-content">
                <button
                    className="primary-next-btn"
                    onClick={handleContinueToReview}
                    disabled={isUpdating || isStageUpdating}
                    style={{
                        opacity: 1,
                        cursor: (isUpdating || isStageUpdating) ? 'not-allowed' : 'pointer'
                    }}
                >
                    <span>Generate Proposal</span>
                    <ArrowRight size={18} />
                </button>
            </div>
        </div>
    );
};

export default GlobalSubmissionActions;
